import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DailyIframe from '@daily-co/daily-js';
import { X, Phone, Video, Mic, MicOff, VideoOff, PhoneOff } from 'lucide-react';

/**
 * Embedded Video/Voice Call Modal using Daily.co
 * Free tier: 10,000 minutes/month, up to 1,000 participants per call
 * Matches the amber/golden theme of the chat widget
 */

// Daily.co free public rooms - no API key needed for basic usage
const DAILY_DOMAIN = 'https://nerdai.daily.co';

/**
 * Inner component that handles the actual call content
 * Keyed by roomId to reset state for each new call
 */
const CallContent = ({ 
  roomId, 
  userName, 
  friendName, 
  isVideoCall, 
  onClose 
}) => {
  const [isConnecting, setIsConnecting] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(!isVideoCall);
  const [participantCount, setParticipantCount] = useState(1);
  const [error, setError] = useState(null);
  
  const callFrameRef = useRef(null);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  // Start call duration timer when connected
  useEffect(() => {
    if (!isConnecting) {
      timerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isConnecting]);

  // Format call duration
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Initialize Daily.co call
  useEffect(() => {
    if (!containerRef.current || !roomId) return;

    const initCall = async () => {
      try {
        // Create the Daily call frame embedded in our container
        const callFrame = DailyIframe.createFrame(containerRef.current, {
          iframeStyle: {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '16px',
          },
          showLeaveButton: false,
          showFullscreenButton: false,
          showLocalVideo: true,
          showParticipantsBar: false,
        });

        callFrameRef.current = callFrame;

        // Set up event listeners
        callFrame.on('joined-meeting', () => {
          setIsConnecting(false);
        });

        callFrame.on('left-meeting', () => {
          onClose();
        });

        callFrame.on('participant-joined', () => {
          setParticipantCount(prev => prev + 1);
        });

        callFrame.on('participant-left', () => {
          setParticipantCount(prev => Math.max(1, prev - 1));
        });

        callFrame.on('error', (e) => {
          console.error('Daily.co error:', e);
          setError(e.errorMsg || 'Call error occurred');
        });

        // Join the room - Daily.co allows creating rooms on-the-fly
        // For free tier, rooms are automatically created when joined
        const roomUrl = `${DAILY_DOMAIN}/${roomId}`;
        
        await callFrame.join({
          url: roomUrl,
          userName: userName,
          startVideoOff: !isVideoCall,
          startAudioOff: false,
        });

        // Set initial states after joining
        setIsMuted(false);
        setIsVideoOff(!isVideoCall);

      } catch (err) {
        console.error('Failed to initialize Daily call:', err);
        setError(err.message || 'Failed to start call');
        setIsConnecting(false);
      }
    };

    initCall();

    // Cleanup on unmount
    return () => {
      if (callFrameRef.current) {
        callFrameRef.current.leave().catch(console.error);
        callFrameRef.current.destroy();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [roomId, userName, isVideoCall, onClose]);

  // Toggle microphone
  const toggleMute = useCallback(() => {
    if (callFrameRef.current) {
      const newMuted = !isMuted;
      callFrameRef.current.setLocalAudio(!newMuted);
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (callFrameRef.current) {
      const newVideoOff = !isVideoOff;
      callFrameRef.current.setLocalVideo(!newVideoOff);
      setIsVideoOff(newVideoOff);
    }
  }, [isVideoOff]);

  // End call
  const endCall = useCallback(() => {
    if (callFrameRef.current) {
      callFrameRef.current.leave().catch(console.error);
    }
    onClose();
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl h-[80vh] mx-4 bg-gradient-to-br from-amber-900 via-amber-800 to-amber-900 rounded-2xl shadow-2xl overflow-hidden border-2 border-amber-500/30">
        
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/60 to-transparent p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isConnecting ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
              <div>
                <p className="text-white font-semibold flex items-center space-x-2">
                  {isVideoCall ? <Video className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                  <span>{isVideoCall ? 'Video' : 'Voice'} Call with {friendName}</span>
                </p>
                <div className="flex items-center space-x-3 text-amber-200 text-sm">
                  {!isConnecting && (
                    <>
                      <span>{formatDuration(callDuration)}</span>
                      <span>•</span>
                      <span>{participantCount} in call</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              title="Minimize"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Daily.co Video Container */}
        <div ref={containerRef} className="w-full h-full relative">
          {/* Connecting Animation Overlay */}
          {isConnecting && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-gradient-to-br from-amber-900 via-amber-800 to-amber-900">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-amber-600/30 animate-ping absolute inset-0" />
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center relative">
                  {isVideoCall ? (
                    <Video className="h-10 w-10 text-white" />
                  ) : (
                    <Phone className="h-10 w-10 text-white" />
                  )}
                </div>
              </div>
              <p className="mt-6 text-white text-lg font-medium">Connecting to {friendName}...</p>
              <p className="text-amber-200 text-sm mt-2">Setting up secure HD connection</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-gradient-to-br from-amber-900 via-amber-800 to-amber-900">
              <div className="w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center">
                <X className="h-10 w-10 text-red-400" />
              </div>
              <p className="mt-6 text-white text-lg font-medium">Connection Error</p>
              <p className="text-amber-200 text-sm mt-2 text-center max-w-md px-4">{error}</p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>

        {/* Custom Controls Overlay */}
        {!error && (
          <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent p-6">
            <div className="flex items-center justify-center space-x-4">
              {/* Mute Button */}
              <button
                onClick={toggleMute}
                className={`p-4 rounded-full transition-all transform hover:scale-110 ${
                  isMuted 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
              </button>

              {/* End Call Button */}
              <button
                onClick={endCall}
                className="p-5 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all transform hover:scale-110 shadow-lg"
                title="End Call"
              >
                <PhoneOff className="h-7 w-7" />
              </button>

              {/* Video Toggle Button */}
              <button
                onClick={toggleVideo}
                className={`p-4 rounded-full transition-all transform hover:scale-110 ${
                  isVideoOff 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
                title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
              >
                {isVideoOff ? <VideoOff className="h-6 w-6" /> : <Video className="h-6 w-6" />}
              </button>
            </div>
            
            {/* Branding */}
            <p className="text-center text-amber-200/50 text-xs mt-4">
              Powered by Nerd AI • HD Video • Secure End-to-End
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Outer wrapper component - handles conditional rendering
 * Uses key prop to ensure CallContent remounts with fresh state for each new call
 */
const VideoCallModal = ({ 
  isOpen, 
  onClose, 
  roomId, 
  userName, 
  userAvatar,
  friendName,
  isVideoCall = true 
}) => {
  // Don't render anything if modal is closed
  if (!isOpen || !roomId) return null;

  // Use portal to render outside the chat widget's DOM hierarchy
  // This ensures the modal appears on top of everything
  return createPortal(
    <CallContent
      key={roomId}
      roomId={roomId}
      userName={userName}
      friendName={friendName}
      isVideoCall={isVideoCall}
      onClose={onClose}
    />,
    document.body
  );
};

export default VideoCallModal;
